// DotLoop API Types

export interface DotloopProfile {
  id: number
  name: string
  type: 'INDIVIDUAL' | 'TEAM' | 'OFFICE' | 'COMPANY'
  default?: boolean
  deactivated?: boolean
}

export interface DotloopTemplate {
  id: number
  name: string
  transactionType: TransactionType
}

export interface DotloopLoop {
  id: number
  name: string
  status: LoopStatus
  transactionType: TransactionType
  loopUrl?: string
  created?: string
  updated?: string
}

export interface DotloopParticipant {
  fullName: string
  email: string
  role: ParticipantRole
  phone?: string
}

export interface DotloopProperty {
  streetNumber?: string
  streetName?: string
  unitNumber?: string
  city?: string
  state?: string
  zipCode?: string
  propertyType?: PropertyType
  bedrooms?: number
  bathrooms?: number
  squareFeet?: number
  lotSize?: string
  yearBuilt?: number
  mlsNumber?: string
  mlsName?: string
  county?: string
}

export interface LoopFinancials {
  purchasePrice?: number
  listingPrice?: number
  contractPrice?: number
  earnestMoney?: number
  commissionRate?: number
  commissionAmount?: number
  sellerCreditAmount?: number
  financingType?: FinancingType
  loanAmount?: number
  downPayment?: number
}

export interface LoopDates {
  contractDate?: string // ISO date string
  closeDate?: string // ISO date string
  listingDate?: string // ISO date string
  expirationDate?: string // ISO date string
  inspectionDate?: string // ISO date string
  appraisalDate?: string // ISO date string
}

export interface LoopCreationRequest {
  profile_id: number
  name: string
  status: LoopStatus
  transaction_type: TransactionType
  loop_template_id?: number
  participants?: DotloopParticipant[]
  property?: DotloopProperty
  financials?: LoopFinancials
  dates?: LoopDates
  notes?: string
}

export interface LoopCreationResponse {
  profile_id: number
  loop_id: number
  loop_url: string
}

export interface AuthStatus {
  authenticated: boolean
  hasRefreshToken: boolean
  expiresAt: number | null
  needsReauth: boolean
  validationError: string | null
  dotloopEmail: string | null
  dotloopAccountId: number | null
}

export interface ApiError {
  error: string
  requestData?: any
}

// Constants from DotLoop API docs
export type TransactionType = 
  | 'PURCHASE_OFFER'
  | 'LISTING_FOR_SALE'
  | 'LISTING_FOR_LEASE'
  | 'LEASE_OFFER'
  | 'COMMERCIAL_PURCHASE_OFFER'
  | 'COMMERCIAL_LISTING_FOR_SALE'
  | 'COMMERCIAL_LISTING_FOR_LEASE'
  | 'COMMERCIAL_LEASE_OFFER'

export type LoopStatus =
  | 'PRE_OFFER'
  | 'PRE_LISTING'
  | 'ACTIVE'
  | 'UNDER_CONTRACT'
  | 'SOLD'
  | 'ARCHIVED'
  | 'CANCELLED'

// Valid roles per Dotloop API docs: https://dotloop.github.io/public-api/#built-in-contactloop-participant-roles
export type ParticipantRole =
  | 'BUYER'
  | 'SELLER'
  | 'LISTING_AGENT'
  | 'BUYING_AGENT'
  | 'LISTING_BROKER'
  | 'BUYING_BROKER'
  | 'TRANSACTION_COORDINATOR'
  | 'LOAN_OFFICER'
  | 'LOAN_PROCESSOR'
  | 'ESCROW_TITLE_REP'
  | 'HOME_INSPECTOR'
  | 'APPRAISER'
  | 'BUYER_ATTORNEY'
  | 'SELLER_ATTORNEY'
  | 'OTHER'

// Display names for UI
export const TRANSACTION_TYPE_LABELS: Record<TransactionType, string> = {
  PURCHASE_OFFER: 'Purchase Offer',
  LISTING_FOR_SALE: 'Listing for Sale',
  LISTING_FOR_LEASE: 'Listing for Lease',
  LEASE_OFFER: 'Lease Offer',
  COMMERCIAL_PURCHASE_OFFER: 'Commercial Purchase',
  COMMERCIAL_LISTING_FOR_SALE: 'Commercial Listing for Sale',
  COMMERCIAL_LISTING_FOR_LEASE: 'Commercial Listing for Lease',
  COMMERCIAL_LEASE_OFFER: 'Commercial Lease Offer'
}

export const LOOP_STATUS_LABELS: Record<LoopStatus, string> = {
  PRE_OFFER: 'Pre-Offer',
  PRE_LISTING: 'Pre-Listing',
  ACTIVE: 'Active',
  UNDER_CONTRACT: 'Under Contract',
  SOLD: 'Sold',
  ARCHIVED: 'Archived',
  CANCELLED: 'Cancelled'
}

export const PARTICIPANT_ROLE_LABELS: Record<ParticipantRole, string> = {
  BUYER: 'Buyer',
  SELLER: 'Seller',
  LISTING_AGENT: 'Listing Agent',
  BUYING_AGENT: "Buyer's Agent",
  LISTING_BROKER: 'Listing Broker',
  BUYING_BROKER: "Buyer's Broker",
  TRANSACTION_COORDINATOR: 'Transaction Coordinator',
  LOAN_OFFICER: 'Loan Officer',
  LOAN_PROCESSOR: 'Loan Processor',
  ESCROW_TITLE_REP: 'Escrow/Title Rep',
  HOME_INSPECTOR: 'Home Inspector',
  APPRAISER: 'Appraiser',
  BUYER_ATTORNEY: "Buyer's Attorney",
  SELLER_ATTORNEY: "Seller's Attorney",
  OTHER: 'Other'
}

// Property and Financial Types
export type PropertyType =
  | 'SINGLE_FAMILY'
  | 'CONDO'
  | 'TOWNHOUSE'
  | 'MULTI_FAMILY'
  | 'LAND'
  | 'COMMERCIAL'
  | 'MOBILE'
  | 'OTHER'

export type FinancingType =
  | 'CASH'
  | 'CONVENTIONAL'
  | 'FHA'
  | 'VA'
  | 'USDA'
  | 'SELLER_FINANCING'
  | 'OTHER'

export const PROPERTY_TYPE_LABELS: Record<PropertyType, string> = {
  SINGLE_FAMILY: 'Single Family',
  CONDO: 'Condominium',
  TOWNHOUSE: 'Townhouse',
  MULTI_FAMILY: 'Multi-Family',
  LAND: 'Land',
  COMMERCIAL: 'Commercial',
  MOBILE: 'Mobile Home',
  OTHER: 'Other'
}

export const FINANCING_TYPE_LABELS: Record<FinancingType, string> = {
  CASH: 'Cash',
  CONVENTIONAL: 'Conventional',
  FHA: 'FHA',
  VA: 'VA',
  USDA: 'USDA',
  SELLER_FINANCING: 'Seller Financing',
  OTHER: 'Other'
}

